import { useState } from "react";
import { rtctApi } from "services/rtctApi";
import { TASK_NAMES, doValue, freshId, parseTasks } from "utils/nodeSchedule";
import { epochTime } from "./NodeStatusCard";
import { currentValue } from "./ScheduleEditor";

// Capture now on the node page (firmware 0.3.7): `do` = "<id>:<tasks>" with a
// fresh id each time (the node ignores an id it has seen). The node's modem
// is off while it sleeps, so the request reaches it at its next upload; it
// then sleeps about 2 minutes, does the tasks and uploads. The read-back says
// what is still to do (do_left) and the last request done (do_done). Reboot
// sends `reboot` = "<id>" after a confirmation: the node restarts once, at
// the end of the wake that received it.
const CHOICES = ["p", "g", "b", "k"];

const tasksWords = (text) => {
  try {
    const tasks = parseTasks(text);
    return tasks.has("k") ? TASK_NAMES.k : ["p", "g", "b", "u"].filter((c) => tasks.has(c)).map((c) => TASK_NAMES[c]).join(", ");
  } catch {
    return text;
  }
};

export default function CaptureNow({ mac, settings, sample, onApplied, confirm = (text) => window.confirm(text) }) {
  const [picked, setPicked] = useState(new Set(["p"]));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const pending = currentValue(settings, "do");
  const left = settings?.reported?.do_left;
  const done = settings?.reported?.do_done;
  const nextUpload = sample?.raw?.next_send ?? sample?.raw?.next_wake;

  const toggle = (c) => {
    const next = new Set(picked);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    setPicked(next);
  };
  const send = async (values, text) => {
    setBusy(true);
    setMessage(null);
    try {
      const answer = await rtctApi.applySettings(mac, values);
      setMessage({ ok: true, text });
      onApplied?.(answer);
    } catch (e) {
      setMessage({ ok: false, text: e?.message || "The request could not be sent" });
    } finally {
      setBusy(false);
    }
  };
  const capture = () => {
    // The node uploads after a capture-now wake anyway; k alone is the whole packet.
    const tasks = picked.has("k") ? new Set(["k"]) : new Set(picked);
    send({ do: doValue(freshId("c"), tasks) }, "Requested. The node picks it up at its next upload and does it about 2 min later.");
  };
  const reboot = () => {
    if (!confirm("Restart the node after its next upload?")) return;
    send({ reboot: freshId("r") }, "Reboot requested. The node restarts after its next upload.");
  };

  return (
    <section className="field-panel" aria-label="Capture now">
      <div className="field-panel-head">
        <div>
          <h2>Capture now</h2>
          <p>Firmware 0.3.7 or later</p>
        </div>
      </div>
      <div className="field-panel-body">
        <div className="field-node-row">
          {CHOICES.map((c) => (
            <label key={c}>
              <input
                type="checkbox"
                checked={picked.has(c)}
                disabled={c !== "k" && picked.has("k")}
                onChange={() => toggle(c)}
              />{" "}
              {TASK_NAMES[c]}
            </label>
          ))}
        </div>
        <p className="field-caption">
          Next upload {epochTime(nextUpload)}, then done about 2 min later. Ground-truth packet: photo pair, GNSS fix
          and radar burst.
        </p>
        <div className="field-actions">
          <button type="button" className="field-button primary" disabled={busy || picked.size === 0} onClick={capture}>
            Capture on the next wake
          </button>
          <button type="button" className="field-button danger" disabled={busy} onClick={reboot}>
            Reboot the node…
          </button>
        </div>
        <dl className="field-node-readback">
          <dt>Asked for</dt>
          <dd>{typeof pending === "string" && pending.includes(":") ? `${tasksWords(pending.split(":")[1])} (${pending.split(":")[0]})` : "nothing"}</dd>
          <dt>Still to do</dt>
          <dd>{left ? tasksWords(String(left)) : "nothing"}</dd>
          <dt>Last done</dt>
          <dd>{done || "none yet"}</dd>
        </dl>
        {message && (
          <div className={message.ok ? "field-notice" : "field-alert"} role="status">
            {message.text}
          </div>
        )}
      </div>
    </section>
  );
}
